import type { StoreApi } from "zustand/vanilla";
import type { VoiceAgentStore, BookingField } from "@moss-js/founding-agent/core";
import {
  fetchAvailability,
  fetchEventConfig,
  formatDayHeader,
  formatSlotLabel,
  formatSlotTime,
  localCalendarDate,
  submitBooking,
} from "@moss-js/founding-agent/core";
import { icon } from "./icons.js";

export interface BookingPublisher {
  publishBookingCompleted(ev: { attendee_name: string; start: string; start_label: string }): void;
  publishBookingFailed(ev: { reason: string }): void;
}

export interface BookingModalOpts {
  serviceUrl: string;
  publishableKey: string;
  publisher: BookingPublisher;
}

export interface BookingModalHandle {
  destroy(): void;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

function getFocusables(el: HTMLElement): HTMLElement[] {
  return Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

// Inside a shadow root, document.activeElement reports the shadow host, not the
// focused control; read from the node's own root (falling back to the document
// when focus sits outside the shadow tree).
function activeElementIn(node: Node): Element | null {
  const rootNode = node.getRootNode();
  return rootNode instanceof ShadowRoot
    ? (rootNode.activeElement ?? document.activeElement)
    : document.activeElement;
}

export function createBookingModal(
  root: HTMLElement,
  store: StoreApi<VoiceAgentStore>,
  opts: BookingModalOpts,
): BookingModalHandle {
  let dialogEl: HTMLDivElement | null = null;
  let backdropEl: HTMLDivElement | null = null;
  let prevFocused: Element | null = null;

  // Local form values (step 2)
  const formValues: Record<string, string> = {};
  // Loading/error state for slots
  let loadingSlots = false;
  let slotsError: string | null = null;
  let loadingFields = false;
  let slotsRetryNonce = 0;

  // Set on destroy so a late-resolving fetch doesn't rebuild a detached modal.
  let disposed = false;

  // Body scroll-lock while the modal is open.
  let scrollLocked = false;
  let prevBodyOverflow = "";
  function lockScroll(): void {
    if (scrollLocked) return;
    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    scrollLocked = true;
  }
  function unlockScroll(): void {
    if (!scrollLocked) return;
    document.body.style.overflow = prevBodyOverflow;
    scrollLocked = false;
  }

  function close(): void {
    // Don't allow dismiss (backdrop / close button / Escape) mid-submit.
    if (store.getState().submitting) return;
    // Clear per-booking state and local form values so a reopen starts fresh
    // (no stale attendee name/email, selected slot, or success/error). Preserve
    // bookingNonce: it is session-scoped (set once per open()), not per-booking,
    // so nulling it would break an agent-re-triggered reopen.
    const nonce = store.getState().bookingNonce;
    store.getState().resetBooking();
    store.getState().setBookingNonce(nonce);
    for (const k of Object.keys(formValues)) delete formValues[k];
  }

  function trapFocus(e: KeyboardEvent): void {
    if (!dialogEl) return;
    const focusables = getFocusables(dialogEl);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = activeElementIn(dialogEl);
    if (e.key === "Tab") {
      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    if (e.key === "Escape") {
      // Stop propagation so Escape does not also reach the global call keyboard
      // handler (bindKeyboard on window), which would end() the whole call.
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  }

  function buildStep1(container: HTMLElement, state: ReturnType<typeof store.getState>): void {
    container.textContent = "";

    if (loadingSlots || (state.slots === null && !slotsError)) {
      container.appendChild(spinner("data-moss-slots-loading", "Loading available times"));
      return;
    }

    if (slotsError) {
      const errEl = document.createElement("div");
      errEl.setAttribute("data-moss-slots-error", "");
      const errText = document.createElement("p");
      errText.textContent = slotsError;
      const retryBtn = document.createElement("button");
      retryBtn.type = "button";
      retryBtn.setAttribute("data-moss-slots-retry", "");
      retryBtn.textContent = "Retry";
      retryBtn.addEventListener("click", () => {
        slotsRetryNonce++;
        slotsError = null;
        loadingSlots = true;
        rerender();
        loadSlots(state);
      });
      errEl.append(icon("alert-circle", 24), errText, retryBtn);
      container.appendChild(errEl);
      return;
    }

    const slots = state.slots ?? {};

    const slotDays = Object.entries(slots)
      .filter(([, daySlots]) => daySlots.length > 0)
      .sort(([a], [b]) => a.localeCompare(b));

    if (slotDays.length === 0) {
      container.appendChild(
        notice(
          "data-moss-slots-empty",
          "No available slots",
          "No times are available in the next 14 days. Please try again later.",
        ),
      );
      return;
    }

    const daysEl = document.createElement("div");
    daysEl.setAttribute("data-moss-slot-days", "");
    for (const [date, daySlots] of slotDays) {
      const dayEl = document.createElement("div");
      dayEl.setAttribute("data-moss-slot-day", date);

      const headerEl = document.createElement("div");
      headerEl.setAttribute("data-moss-slot-day-header", "");
      const dayLabel = document.createElement("p");
      dayLabel.textContent = formatDayHeader(date);
      const rule = document.createElement("span");
      rule.setAttribute("data-moss-slot-day-rule", "");
      headerEl.append(dayLabel, rule);
      dayEl.appendChild(headerEl);

      const gridEl = document.createElement("div");
      gridEl.setAttribute("data-moss-slot-grid", "");
      for (const slot of daySlots) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.setAttribute("data-moss-slot", slot.start);
        btn.textContent = formatSlotTime(slot.start);
        btn.addEventListener("click", () => {
          const label = formatSlotLabel(slot.start);
          store.getState().setSelectedStart(slot.start, label);
          store.getState().setBookingStep(2);
        });
        gridEl.appendChild(btn);
      }
      dayEl.appendChild(gridEl);
      daysEl.appendChild(dayEl);
    }
    container.appendChild(daysEl);
  }

  function spinner(attr: string, label: string): HTMLDivElement {
    const el = document.createElement("div");
    el.setAttribute(attr, "");
    el.setAttribute("role", "status");
    el.setAttribute("aria-label", label);
    el.appendChild(icon("loader-2", 20));
    return el;
  }

  function notice(attr: string, title: string, detail: string): HTMLDivElement {
    const el = document.createElement("div");
    el.setAttribute(attr, "");
    const titleEl = document.createElement("p");
    titleEl.textContent = title;
    const detailEl = document.createElement("p");
    detailEl.textContent = detail;
    el.append(icon("calendar", 32), titleEl, detailEl);
    return el;
  }

  function errorBox(attr: string, message: string): HTMLDivElement {
    const el = document.createElement("div");
    el.setAttribute(attr, "");
    el.setAttribute("role", "alert");
    el.setAttribute("aria-live", "assertive");
    const textEl = document.createElement("p");
    textEl.textContent = message;
    el.append(icon("alert-circle", 14), textEl);
    return el;
  }

  function field(id: string, label: string, required: boolean, control: HTMLElement): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-moss-booking-field", "");
    const labelEl = document.createElement("label");
    labelEl.setAttribute("for", id);
    labelEl.textContent = label;
    if (required) {
      const star = document.createElement("span");
      star.setAttribute("data-moss-booking-required", "");
      star.setAttribute("aria-hidden", "true");
      star.textContent = "*";
      labelEl.appendChild(star);
    }
    wrap.append(labelEl, control);
    return wrap;
  }

  function buildStep2(container: HTMLElement, state: ReturnType<typeof store.getState>): void {
    container.textContent = "";

    const { selectedStartLabel, fields, submitting, bookingError, bookingResult, bookingNonce } = state;

    if (bookingResult) {
      const successEl = document.createElement("div");
      successEl.setAttribute("data-moss-booking-success", "");
      successEl.setAttribute("role", "status");
      successEl.setAttribute("tabindex", "-1");
      const textEl = document.createElement("div");
      textEl.setAttribute("data-moss-booking-success-text", "");
      const headline = document.createElement("p");
      headline.textContent = "You're booked!";
      const when = document.createElement("p");
      when.textContent = selectedStartLabel ?? formatSlotLabel(bookingResult.start);
      textEl.append(headline, when);
      const note = document.createElement("p");
      note.setAttribute("data-moss-booking-success-note", "");
      note.textContent = "A confirmation has been sent to your email.";
      successEl.append(icon("check-circle-2", 40), textEl, note);
      container.appendChild(successEl);
      return;
    }

    if (selectedStartLabel) {
      const reminderEl = document.createElement("div");
      reminderEl.setAttribute("data-moss-selected-slot", "");
      const reminderText = document.createElement("p");
      reminderText.textContent = selectedStartLabel;
      reminderEl.append(icon("clock", 14), reminderText);
      container.appendChild(reminderEl);
    }

    // `fields === null` means fetchEventConfig has not returned; an empty array
    // means "no extra fields" and the form renders.
    if (fields === null) {
      container.appendChild(
        bookingError
          ? errorBox("data-moss-fields-error", bookingError)
          : spinner("data-moss-fields-loading", "Loading form"),
      );
      return;
    }

    const form = document.createElement("form");
    form.setAttribute("data-moss-booking-form", "");

    const nameInput = document.createElement("input");
    nameInput.id = "bf-name";
    nameInput.type = "text";
    nameInput.required = true;
    nameInput.setAttribute("aria-required", "true");
    nameInput.autocomplete = "name";
    nameInput.placeholder = "Your name";
    nameInput.value = formValues["name"] ?? "";
    nameInput.addEventListener("input", () => {
      formValues["name"] = nameInput.value;
    });
    form.appendChild(field("bf-name", "Name", true, nameInput));

    const emailInput = document.createElement("input");
    emailInput.id = "bf-email";
    emailInput.type = "email";
    emailInput.required = true;
    emailInput.setAttribute("aria-required", "true");
    emailInput.autocomplete = "email";
    emailInput.placeholder = "you@example.com";
    emailInput.value = formValues["email"] ?? "";
    emailInput.addEventListener("input", () => {
      formValues["email"] = emailInput.value;
    });
    form.appendChild(field("bf-email", "Email", true, emailInput));

    // Extra fields
    const visibleFields: BookingField[] = fields
      ? fields.filter(
          (f) =>
            !f.hidden &&
            f.slug !== "name" &&
            f.slug !== "email" &&
            f.slug !== "rescheduleReason",
        )
      : [];

    for (const f of visibleFields) {
      const fieldLabel = f.label ?? f.slug.replace(/_/g, " ");

      if (f.type === "textarea") {
        const textarea = document.createElement("textarea");
        textarea.id = "bf-" + f.slug;
        textarea.required = f.required;
        textarea.rows = 3;
        textarea.value = formValues[f.slug] ?? "";
        textarea.addEventListener("input", () => {
          formValues[f.slug] = textarea.value;
        });
        form.appendChild(field("bf-" + f.slug, fieldLabel, f.required, textarea));
      } else {
        const input = document.createElement("input");
        input.id = "bf-" + f.slug;
        input.type = f.type === "email" ? "email" : "text";
        input.required = f.required;
        input.value = formValues[f.slug] ?? "";
        input.addEventListener("input", () => {
          formValues[f.slug] = input.value;
        });
        form.appendChild(field("bf-" + f.slug, fieldLabel, f.required, input));
      }
    }

    if (bookingError) form.appendChild(errorBox("data-moss-booking-error", bookingError));

    const submitBtn = document.createElement("button");
    submitBtn.type = "submit";
    submitBtn.setAttribute("data-moss-booking-submit", "");
    submitBtn.disabled = submitting;
    if (submitting) submitBtn.append(icon("loader-2"), "Booking...");
    else submitBtn.textContent = bookingError ? "Try Again" : "Confirm Booking";
    form.appendChild(submitBtn);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const s = store.getState();
      if (!s.selectedStart || !s.bookingNonce) return;
      void submitBooking({
        serviceUrl: opts.serviceUrl,
        publishableKey: opts.publishableKey,
        nonce: s.bookingNonce,
        selectedStart: s.selectedStart,
        selectedStartLabel: s.selectedStartLabel,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        fields: s.fields,
        formValues: { ...formValues },
        setSubmitting: (v) => { if (!disposed) store.getState().setSubmitting(v); },
        setBookingResult: (r) => { if (!disposed) store.getState().setBookingResult(r); },
        setBookingError: (err) => { if (!disposed) store.getState().setBookingError(err); },
        onCompleted: (ev) => { if (!disposed) opts.publisher.publishBookingCompleted(ev); },
        onFailed: (ev) => { if (!disposed) opts.publisher.publishBookingFailed(ev); },
      });
    });

    container.appendChild(form);
  }

  async function loadSlots(state: ReturnType<typeof store.getState>): Promise<void> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();
    const from = localCalendarDate(now, timeZone);
    const to14 = localCalendarDate(new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000), timeZone);
    try {
      const slots = await fetchAvailability(
        opts.serviceUrl,
        opts.publishableKey,
        from,
        to14,
        timeZone,
      );
      if (disposed || !store.getState().bookingOpen) return;
      store.getState().setSlots(slots);
      slotsError = null;
    } catch {
      if (disposed || !store.getState().bookingOpen) return;
      slotsError = "Could not load available times. Please try again.";
    } finally {
      loadingSlots = false;
      if (!disposed) rerender();
    }
  }

  async function loadFields(): Promise<void> {
    try {
      const config = await fetchEventConfig(opts.serviceUrl, opts.publishableKey);
      if (disposed || !store.getState().bookingOpen) return;
      store.getState().setFields(config.fields);
    } catch {
      if (disposed || !store.getState().bookingOpen) return;
      store.getState().setBookingError("Could not load booking form. Please try again.");
    } finally {
      loadingFields = false;
      if (!disposed) rerender();
    }
  }

  function buildDialog(state: ReturnType<typeof store.getState>): void {
    if (backdropEl) {
      backdropEl.remove();
      backdropEl = null;
    }
    if (dialogEl) {
      dialogEl.remove();
      dialogEl = null;
    }
    document.removeEventListener("keydown", trapFocus);

    if (!state.bookingOpen) {
      if (prevFocused && prevFocused instanceof HTMLElement) {
        prevFocused.focus();
        prevFocused = null;
      }
      return;
    }

    // Backdrop
    backdropEl = document.createElement("div");
    backdropEl.setAttribute("data-moss-booking-backdrop", "");
    backdropEl.setAttribute("aria-hidden", "true");
    backdropEl.addEventListener("click", close);
    root.appendChild(backdropEl);

    // Dialog
    dialogEl = document.createElement("div");
    dialogEl.setAttribute("role", "dialog");
    dialogEl.setAttribute("aria-modal", "true");
    dialogEl.setAttribute("aria-label", "Book a meeting");
    dialogEl.setAttribute("data-moss-booking-modal", "");
    // The slot picker is a long list, so it gets a capped scroll box; the
    // details and confirmation steps size to their content.
    dialogEl.setAttribute(
      "data-moss-booking-step",
      state.bookingStep === 1 && !state.bookingResult ? "1" : "2",
    );

    // Header
    const headerEl = document.createElement("div");
    headerEl.setAttribute("data-moss-booking-header", "");
    const headingEl = document.createElement("div");
    headingEl.setAttribute("data-moss-booking-heading", "");

    if (state.bookingStep === 2 && !state.bookingResult) {
      const backBtn = document.createElement("button");
      backBtn.type = "button";
      backBtn.setAttribute("data-moss-booking-back", "");
      backBtn.setAttribute("aria-label", "Back to slot picker");
      backBtn.appendChild(icon("chevron-left", 16));
      backBtn.addEventListener("click", () => {
        store.getState().setBookingStep(1);
        store.getState().setBookingError(null);
      });
      headingEl.appendChild(backBtn);
    }

    const titleEl = document.createElement("span");
    titleEl.setAttribute("data-moss-booking-title", "");
    titleEl.textContent = state.bookingResult
      ? "Booking Confirmed"
      : state.bookingStep === 1
        ? "Pick a Time"
        : "Your Details";
    headingEl.appendChild(titleEl);
    headerEl.appendChild(headingEl);

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.setAttribute("data-moss-booking-close", "");
    closeBtn.setAttribute("aria-label", "Close booking form");
    closeBtn.setAttribute("title", "Close");
    closeBtn.appendChild(icon("x", 14));
    closeBtn.addEventListener("click", close);
    headerEl.appendChild(closeBtn);

    dialogEl.appendChild(headerEl);

    // Body
    const bodyEl = document.createElement("div");
    bodyEl.setAttribute("data-moss-booking-body", "");

    if (!state.bookingNonce) {
      bodyEl.appendChild(
        notice(
          "data-moss-booking-unavailable",
          "Booking unavailable",
          "Demo scheduling is not configured for this agent.",
        ),
      );
    } else if (state.bookingStep === 1) {
      buildStep1(bodyEl, state);
    } else {
      buildStep2(bodyEl, state);
    }

    dialogEl.appendChild(bodyEl);

    const footerEl = document.createElement("div");
    footerEl.setAttribute("data-moss-booking-footer", "");
    const footerText = document.createElement("p");
    const brand = document.createElement("span");
    brand.textContent = "moss";
    footerText.append("powered by ", brand);
    footerEl.appendChild(footerText);
    dialogEl.appendChild(footerEl);

    root.appendChild(dialogEl);

    // Set up focus trap
    document.addEventListener("keydown", trapFocus);

    // Focus: prefer the success status node (announced), else the first
    // focusable, else the dialog itself. Runs after the dialog is attached.
    const successNode = dialogEl.querySelector<HTMLElement>("[data-moss-booking-success]");
    if (successNode) {
      successNode.focus();
    } else {
      const focusables = getFocusables(dialogEl);
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        dialogEl.setAttribute("tabindex", "-1");
        dialogEl.focus();
      }
    }
  }

  let prevBookingOpen = false;
  let prevBookingStep: 1 | 2 = 1;
  let prevSlots: ReturnType<typeof store.getState>["slots"] = null;
  let prevFields: ReturnType<typeof store.getState>["fields"] = null;
  let prevSubmitting = false;
  let prevBookingResult: ReturnType<typeof store.getState>["bookingResult"] = null;
  let prevBookingError: ReturnType<typeof store.getState>["bookingError"] = null;
  let prevSelectedStart: string | null = null;
  let prevSelectedStartLabel: string | null = null;
  let prevBookingNonce: string | null = null;
  let prevLoadingSlots = false;
  let prevSlotsError: string | null = null;
  let prevLoadingFields = false;

  function rerender(): void {
    if (disposed) return;
    const state = store.getState();

    // Rebuild only when a structural input changed. The store subscription
    // fires on every write, and audio levels tick ~25Hz during a call; without
    // this gate buildDialog would tear the dialog down and rebuild it — resetting
    // focus to the first field — dozens of times a second, so the visitor could
    // never type. Local loading flags call rerender() directly, so they are part
    // of the signature too.
    const structuralChanged =
      state.bookingOpen !== prevBookingOpen ||
      state.bookingStep !== prevBookingStep ||
      state.slots !== prevSlots ||
      state.fields !== prevFields ||
      state.submitting !== prevSubmitting ||
      state.bookingResult !== prevBookingResult ||
      state.bookingError !== prevBookingError ||
      state.selectedStart !== prevSelectedStart ||
      state.selectedStartLabel !== prevSelectedStartLabel ||
      state.bookingNonce !== prevBookingNonce ||
      loadingSlots !== prevLoadingSlots ||
      slotsError !== prevSlotsError ||
      loadingFields !== prevLoadingFields;

    if (structuralChanged) {
      // Capture opener before buildDialog shifts focus into the dialog.
      if (!prevBookingOpen && state.bookingOpen) {
        prevFocused = activeElementIn(root);
        lockScroll();
      }
      if (prevBookingOpen && !state.bookingOpen) {
        unlockScroll();
      }

      buildDialog(state);

      // Kick loads whenever the open modal needs data it doesn't have. Step 1
      // is not gated on the open transition: the slot_selected protocol event
      // can open directly at step 2, so Back must still trigger the slot load
      // (slotsError is excluded; its Retry button drives that path).
      if (
        state.bookingOpen &&
        state.bookingStep === 1 &&
        state.slots === null &&
        !loadingSlots &&
        !slotsError
      ) {
        loadingSlots = true;
        void loadSlots(state);
      }
      if (state.bookingOpen && state.bookingStep === 2 && (!prevBookingOpen || prevBookingStep === 1)) {
        if (state.fields === null && !loadingFields) {
          loadingFields = true;
          void loadFields();
        }
      }
    }

    prevBookingOpen = state.bookingOpen;
    prevBookingStep = state.bookingStep;
    prevSlots = state.slots;
    prevFields = state.fields;
    prevSubmitting = state.submitting;
    prevBookingResult = state.bookingResult;
    prevBookingError = state.bookingError;
    prevSelectedStart = state.selectedStart;
    prevSelectedStartLabel = state.selectedStartLabel;
    prevBookingNonce = state.bookingNonce;
    prevLoadingSlots = loadingSlots;
    prevSlotsError = slotsError;
    prevLoadingFields = loadingFields;
  }

  // Initial render
  rerender();

  const unsub = store.subscribe(() => rerender());

  return {
    destroy() {
      disposed = true;
      unsub();
      document.removeEventListener("keydown", trapFocus);
      unlockScroll();
      backdropEl?.remove();
      backdropEl = null;
      dialogEl?.remove();
      dialogEl = null;
    },
  };
}
